import React, { useEffect, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { Bus, constants, SiteConfigManager, usePaymentMethods } from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { EmptyStateView, HeaderTitle, Loader } from '@atlas/ui';

import FakePage from 'common/Popup/FakePage';
import usePopup from 'common/Popup/usePopup';
import StaticBlock from 'common/StaticBlock/StaticBlock';
import SeoPageText from 'components/SeoPage/SeoPageText';

import PaymentListItem from '../PaymentListItem';

import WithdrawalPayment from './WithdrawalPayment';

const { payment } = clientProfileRoutes;
const { withdrawal } = paymentRoutes;
const { CLIENT_PROFILE } = COMPONENT_TYPES;

const WithdrawalList = defaultValue => {
  const {
    params: { paymentMethod },
    url,
  } = useRouteMatch();
  const { isOpenPopup, setOpenPopup } = usePopup();
  const { isLoading, paymentMethods, updatePaymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.WITHDRAWAL,
    constants.DEVICE_TYPES.MOBILE,
  );
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
  const history = useHistory();

  useEffect(() => {
    setOpenPopup(!!paymentMethod);
  }, [paymentMethod, setOpenPopup]);

  useEffect(() => {
    Bus.on(Bus.events.profile.withdrawalComplete, updatePaymentMethods);

    return () => Bus.off(Bus.events.profile.withdrawalComplete, updatePaymentMethods);
  }, [updatePaymentMethods]);

  const renderWithdrawalPaymentPage = useMemo(() => {
    if (!paymentMethods.length || !paymentMethod) {
      return null;
    }

    const paymentMethodData = paymentMethods.find(method => method.id === Number(paymentMethod));

    if (!paymentMethodData) {
      history.push(`${clientProfilePath}/${payment}/${withdrawal}`);
    }

    return (
      <FakePage className="user-profile" isOpen={isOpenPopup}>
        <WithdrawalPayment paymentMethod={paymentMethodData} />
      </FakePage>
    );
  }, [paymentMethods, paymentMethod, isOpenPopup, history, clientProfilePath]);

  if (isLoading) {
    return <Loader className="relative" />;
  }

  if (!paymentMethods) {
    return (
      <>
        <div className="payments-list empty">
          <EmptyStateView description="withdrawal_empty_message" title="withdrawal_empty_title" />
        </div>
        {renderWithdrawalPaymentPage}
        <SeoPageText />
      </>
    );
  }

  return (
    <>
      {paymentMethods.length > 0 && (
        <div className="title-wrapper bottom-space">
          <HeaderTitle className="sub-text block-medium" title="select_payment_system" />
        </div>
      )}
      {paymentMethods.length === 0 ? (
        (() => {
          const staticBlockConfig = SiteConfigManager.getConfig(
            'config.config.useStaticBlocksFromConfig',
            defaultValue,
          );

          if (staticBlockConfig) {
            return (
              <StaticBlock
                fallback={
                  <EmptyStateView
                    className="viewport-height"
                    description="withdrawal_empty_message"
                    title="withdrawal_empty_title"
                  />
                }
                options={{ source: 'withdrawal-empty-info-mobile' }}
              />
            );
          }

          return (
            <EmptyStateView
              className="viewport-height"
              description="withdrawal_empty_message"
              title="withdrawal_empty_title"
            />
          );
        })()
      ) : (
        <>
          {paymentMethods.map(payment => {
            const { id } = payment;

            return (
              <PaymentListItem
                key={payment.id}
                methodAsLink
                operation="withdrawal"
                payment={payment}
                to={`${url}/${id}`}
              />
            );
          })}
        </>
      )}
      {renderWithdrawalPaymentPage}
      <SeoPageText />
    </>
  );
};

export default WithdrawalList;
